import type { Client, ClientInputs, ClientOutputs } from '@botpress/client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useQueryKeys } from '~/hooks'
import { botQueryOptions } from '~/queries/bots'

type BotIntegrations = NonNullable<ClientOutputs['getBot']['bot']['integrations']>
type BotSubscriptionsEvents = ClientOutputs['getBot']['bot']['subscriptions']['events']
type BotSubscriptionsEventsInput = NonNullable<ClientInputs['updateBot']['subscriptions']>['events']

// Platform events generated by the runtime, not defined in any integration. These must always
// stay whitelisted so toggling integration events never kills core bot processing.
const PLATFORM_EVENTS = ['message_created_trigger', 'workflow_update', 'state_expired'] as const

const collectIntegrationEventKeys = (integrations: BotIntegrations): Record<string, Set<string>> => {
  const result: Record<string, Set<string>> = {}
  for (const entry of Object.values(integrations ?? {})) {
    if (!entry?.name) continue
    const events = (entry as { events?: Record<string, unknown> }).events
    result[entry.name] = new Set(Object.keys(events ?? {}))
  }
  return result
}

const buildAllKnownEventSubscriptions = (integrations: BotIntegrations): Record<string, {}> => {
  const subscriptions: Record<string, {}> = {}
  for (const entry of Object.values(integrations ?? {})) {
    if (!entry?.name) continue
    const events = (entry as { events?: Record<string, unknown> }).events
    for (const eventKey of Object.keys(events ?? {})) {
      subscriptions[`${entry.name}:${eventKey}`] = {}
    }
  }
  return subscriptions
}

const buildEnabledEventsMap = (
  integrations: BotIntegrations,
  subscriptionsEvents: BotSubscriptionsEvents | null | undefined
): Record<string, Set<string>> => {
  if (subscriptionsEvents === null) {
    // null = "all events for all installed integrations are enabled"
    return collectIntegrationEventKeys(integrations)
  }
  if (!subscriptionsEvents) {
    return {}
  }

  const result: Record<string, Set<string>> = {}
  for (const [fullEventName, subscription] of Object.entries(subscriptionsEvents)) {
    if (subscription === null) continue
    const separatorIdx = fullEventName.indexOf(':')
    if (separatorIdx < 0) continue
    const integrationName = fullEventName.slice(0, separatorIdx)
    const eventKey = fullEventName.slice(separatorIdx + 1)
    if (!result[integrationName]) {
      result[integrationName] = new Set<string>()
    }
    result[integrationName].add(eventKey)
  }
  return result
}

type UseBotEventSubscriptionsOptions = {
  client: Client
  workspaceId: string
  botId: string
  canUpdate: boolean
  onError?: (error: unknown) => void
}

export const useBotEventSubscriptions = ({
  client,
  workspaceId,
  botId,
  canUpdate,
  onError,
}: UseBotEventSubscriptionsOptions) => {
  const queryClient = useQueryClient()
  const { getBot: getBotQueryKey } = useQueryKeys()
  const botQuery = useQuery(botQueryOptions({ workspaceId, botId }))

  const enabledEventsByIntegration = useMemo(
    () => buildEnabledEventsMap(botQuery.data?.integrations ?? {}, botQuery.data?.subscriptions?.events),
    [botQuery.data?.integrations, botQuery.data?.subscriptions?.events]
  )

  const toggleMutation = useMutation({
    mutationFn: async ({
      integrationName,
      eventKey,
      enabled,
    }: {
      integrationName: string
      eventKey: string
      enabled: boolean
    }) => {
      const bot = botQuery.data
      const integrations = bot?.integrations ?? {}
      const currentSubscriptions = bot?.subscriptions?.events
      const eventType = `${integrationName}:${eventKey}`

      let nextSubscriptions: BotSubscriptionsEventsInput
      if (currentSubscriptions === null && enabled) {
        nextSubscriptions = null
      } else {
        const base =
          currentSubscriptions === null
            ? buildAllKnownEventSubscriptions(integrations)
            : { ...((currentSubscriptions ?? {}) as Record<string, {} | null>) }

        for (const platformEvent of PLATFORM_EVENTS) {
          if (!(platformEvent in base)) {
            base[platformEvent] = {}
          }
        }

        base[eventType] = enabled ? {} : null
        nextSubscriptions = base
      }

      await client.updateBot({
        id: botId,
        subscriptions: { events: nextSubscriptions },
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getBotQueryKey(botId) }),
    onError,
  })

  const handleToggleEvent = async (integrationName: string, eventKey: string, enabled: boolean) => {
    try {
      await toggleMutation.mutateAsync({ integrationName, eventKey, enabled })
    } catch {
      // onError handler receives the error via the mutation; swallow here to avoid unhandled rejection.
    }
  }

  const isEventToggleDisabled = !canUpdate || toggleMutation.isPending

  return { enabledEventsByIntegration, handleToggleEvent, isEventToggleDisabled }
}
